import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, TriangleAlert } from 'lucide-react';
import { z } from 'zod';
import { CharacterCounter } from '@/components/common/CharacterCounter';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { PCMP_EDIT_LIMITS } from '../../constants/pcmpProposal.constants';
import { useEditPcmpProposalSection } from '../../hooks/useEditPcmpProposalSection';
import type { PcmpProposalDetail } from '../../types/pcmpProposal.types';
import {
  blankToNull,
  pcmpEditReasonSchema,
  pcmpOptionalTextSchema,
} from '../../utils/pcmpProposalEditForm';
import { PcmpEditDialog } from './PcmpEditDialog';
import { PcmpEditReasonField } from './PcmpEditReasonField';
import { PcmpInstitutePicker } from './PcmpInstitutePicker';

const instituteFormSchema = z.object({
  institute: z
    .object({ id: z.number(), name: z.string(), place: z.string().nullable() })
    .nullable()
    .refine((value) => value !== null, 'Institute is required'),
  comments: pcmpOptionalTextSchema(PCMP_EDIT_LIMITS.submissionComments),
  reason: pcmpEditReasonSchema,
});

type InstituteFormValues = z.input<typeof instituteFormSchema>;

interface PcmpInstituteEditDialogProps {
  proposal: PcmpProposalDetail;
  onClose: () => void;
}

/** Legacy EditInstituteInfo: move the submission to another assigned institute. */
export function PcmpInstituteEditDialog({ proposal, onClose }: PcmpInstituteEditDialogProps) {
  const { institute } = proposal;
  const form = useForm<InstituteFormValues>({
    resolver: zodResolver(instituteFormSchema),
    defaultValues: {
      institute: {
        id: institute.id,
        name: institute.name ?? `Institute #${institute.id}`,
        place: [institute.thanaName, institute.districtName].filter(Boolean).join(', ') || null,
      },
      comments: institute.submissionComments ?? '',
      reason: '',
    },
  });
  const { mutate, isPending, error } = useEditPcmpProposalSection(
    proposal.proposalId,
    'institute',
    onClose,
  );

  const onSubmit = form.handleSubmit((values) => {
    if (!values.institute) return;
    mutate({
      instituteId: values.institute.id,
      comments: blankToNull(values.comments),
      reason: values.reason,
    });
  });

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title="Edit School"
        icon={Building2}
        capabilities={proposal.capabilities}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
      >
        <div className="flex items-start gap-3 rounded-lg bg-warning px-4 py-3 text-sm text-warning-foreground">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            The school belongs to the whole submission: every proposal of it moves to the new
            institute.
          </span>
        </div>
        <FormField
          control={form.control}
          name="institute"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>
                Institute <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <PcmpInstitutePicker
                  userId={proposal.mo.userId}
                  value={field.value}
                  onChange={field.onChange}
                  aria-invalid={fieldState.invalid}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="comments"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between gap-2">
                <FormLabel>Submission note</FormLabel>
                <CharacterCounter
                  current={field.value.length}
                  max={PCMP_EDIT_LIMITS.submissionComments}
                />
              </div>
              <FormControl>
                <Textarea {...field} rows={2} maxLength={PCMP_EDIT_LIMITS.submissionComments} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <PcmpEditReasonField control={form.control} />
      </PcmpEditDialog>
    </Form>
  );
}
